import { Anchor, BodyExtraSmall, BodySmall, Stepper } from '@taxbit-private/cosmic'
import {
  COSMIC_VALUE_PLACEHOLDER,
  useCosmicLocalizationContext,
} from '@taxbit-private/cosmic-localization'
import { useMemo } from 'react'

type FailedStateStepperProps = {
  lastSuccessfulUpdate?: string | null
  mostRecentFailedUpdate?: string | null
  nextUpdateScheduled?: string | null
}

const FailedStateStepper = ({
  lastSuccessfulUpdate,
  mostRecentFailedUpdate,
  nextUpdateScheduled,
}: FailedStateStepperProps) => {
  const { formatDateTime } = useCosmicLocalizationContext()

  const steps = useMemo(
    () => [
      {
        key: 'last-successful-update',
        title: 'Last successful update',
        titleUtilityElement: (
          <BodyExtraSmall colorVariant="secondary">
            {lastSuccessfulUpdate
              ? formatDateTime({
                  date: lastSuccessfulUpdate,
                  format: 'DateTimeShortened',
                  tz: 'Browser',
                })
              : COSMIC_VALUE_PLACEHOLDER}
          </BodyExtraSmall>
        ),
        titleUtilityElementPlacement: 'right' as const,
        content: <></>,
        avatarProps: { iconName: 'check-circle' as const, variant: 'success' as const },
      },
      {
        key: 'update-failed',
        title: 'Update failed',
        titleUtilityElement: (
          <BodyExtraSmall colorVariant="secondary">
            {mostRecentFailedUpdate
              ? formatDateTime({
                  date: mostRecentFailedUpdate,
                  format: 'DateTimeShortened',
                  tz: 'Browser',
                })
              : COSMIC_VALUE_PLACEHOLDER}
          </BodyExtraSmall>
        ),
        titleUtilityElementPlacement: 'right' as const,
        content: (
          <BodySmall colorVariant="secondary">
            There was an error processing your data and this workspace may not reflect the most
            recent changes. Please{' '}
            <Anchor
              href="https://info.taxbit.com/accounting"
              label="contact"
              variant="anchor-primary-inline"
              trackingId="data-status-contact-support-link"
            />{' '}
            support if this does not resolve within 24 hours.
          </BodySmall>
        ),
        avatarProps: { iconName: 'alert-circle' as const, variant: 'danger' as const },
      },
      {
        key: 'next-update-scheduled',
        title: 'Next update scheduled',
        titleUtilityElement: (
          <BodyExtraSmall colorVariant="secondary">
            {nextUpdateScheduled
              ? formatDateTime({
                  date: nextUpdateScheduled,
                  format: 'DateTimeShortened',
                  tz: 'Browser',
                })
              : COSMIC_VALUE_PLACEHOLDER}
          </BodyExtraSmall>
        ),
        titleUtilityElementPlacement: 'right' as const,
        content: <></>,
        avatarProps: { iconName: 'clock' as const, variant: 'secondary' as const },
      },
    ],
    [lastSuccessfulUpdate, mostRecentFailedUpdate, nextUpdateScheduled, formatDateTime]
  )

  return <Stepper currentStep={1} steps={steps} />
}

export default FailedStateStepper
